import { useEffect, useMemo, useRef } from "react";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  reorderChapterIds,
  type ChapterView,
} from "@/features/story";
import { cn } from "@/lib/utils";

type ChapterListProps = {
  activeChapterId: string | null;
  chapters: ChapterView[];
  onOpenChapter: (chapterId: string) => void;
  onReorderChapters: (orderedChapterIds: string[]) => Promise<void>;
};

type SortableChapterCardProps = {
  activeChapterId: string | null;
  chapter: ChapterView;
  chapterButtonRefs: React.MutableRefObject<
    Record<string, HTMLButtonElement | null>
  >;
  chapterCount: number;
  index: number;
  onOpenChapter: (chapterId: string) => void;
};

function SortableChapterCard({
  activeChapterId,
  chapter,
  chapterButtonRefs,
  chapterCount,
  index,
  onOpenChapter,
}: SortableChapterCardProps) {
  const { t } = useTranslation();
  const isClosing = index === chapterCount - 1;
  const isOpening = index === 0;
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: chapter.id });

  return (
    <div
      ref={setNodeRef}
      data-testid="chapter-card"
      className={cn(
        "rounded-[1.15rem] px-3 py-3 transition-colors",
        chapter.id === activeChapterId
          ? "bg-[#9d5200] text-canvas"
          : "bg-transparent text-muted-foreground hover:bg-[#f4eadc]",
        isDragging ? "z-10 shadow-sm" : undefined,
      )}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <div className="flex items-start gap-3">
        <button
          aria-label={t("story.chapters.dragHandle")}
          className={cn(
            "mt-0.5 rounded-sm p-0.5 transition-colors",
            chapter.id === activeChapterId
              ? "text-canvas/80 hover:text-canvas"
              : "text-muted-foreground hover:text-foreground",
          )}
          type="button"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>

        <div className="min-w-0 flex-1">
          <button
            aria-current={chapter.id === activeChapterId ? "page" : undefined}
            className="w-full text-left"
            onClick={() => onOpenChapter(chapter.id)}
            ref={(element) => {
              chapterButtonRefs.current[chapter.id] = element;
            }}
            type="button"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p
                  className={cn(
                    "truncate text-[1.05rem] font-medium",
                    chapter.id === activeChapterId
                      ? "text-canvas"
                      : "text-foreground/80",
                  )}
                >
                  {chapter.title}
                </p>
                <p
                  className={cn(
                    "mt-1 text-[11px] uppercase tracking-[0.16em]",
                    chapter.id === activeChapterId
                      ? "text-canvas/75"
                      : "text-muted-foreground",
                  )}
                >
                  {t(`story.statuses.${chapter.status}`)}
                  {isOpening ? ` | ${t("story.chapters.opening")}` : ""}
                  {isClosing ? ` | ${t("story.chapters.closing")}` : ""}
                </p>
              </div>
              <div className="ml-3 flex shrink-0 items-start gap-4">
                <span
                  className={cn(
                    "pt-0.5 text-sm font-medium",
                    chapter.id === activeChapterId
                      ? "text-canvas/85"
                      : "text-foreground/65",
                  )}
                >
                  {index + 1}
                </span>
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

export function ChapterList({
  activeChapterId,
  chapters,
  onOpenChapter,
  onReorderChapters,
}: ChapterListProps) {
  const chapterButtonRefs = useRef<Record<string, HTMLButtonElement | null>>(
    {},
  );
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const orderedChapterIds = useMemo(
    () => chapters.map((chapter) => chapter.id),
    [chapters],
  );

  useEffect(() => {
    if (!activeChapterId) {
      return;
    }

    chapterButtonRefs.current[activeChapterId]?.focus();
  }, [activeChapterId]);

  function handleDragEnd(event: DragEndEvent) {
    if (!event.over || event.active.id === event.over.id) {
      return;
    }

    const nextOrderedIds = reorderChapterIds(
      orderedChapterIds,
      String(event.active.id),
      String(event.over.id),
    );

    if (nextOrderedIds !== orderedChapterIds) {
      void onReorderChapters(nextOrderedIds);
    }
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <SortableContext
        items={orderedChapterIds}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-3">
          {chapters.map((chapter, index) => (
            <SortableChapterCard
              activeChapterId={activeChapterId}
              chapter={chapter}
              chapterButtonRefs={chapterButtonRefs}
              chapterCount={chapters.length}
              index={index}
              key={chapter.id}
              onOpenChapter={onOpenChapter}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
